import { EUserSubRole } from '@common/enums/user-sub-role.enum';
import { EUsersRole } from '@common/enums/users-role.enum';
import { BitfStrapiModel } from '@common/libs/bitforce/core/models';
import { Agency } from './agency.model';
import { Booklet } from './booklet.model';
import { Role } from './role.model';
import { StrapiFile } from './strapi-file.model';

export class User extends BitfStrapiModel {
  name: string;
  surname: string;
  username: string;
  phoneNumber: number;
  email: string;
  provider: string;
  confirmed: boolean;
  blocked: boolean;
  publishedAt: string;
  locale?: string;
  linkAgentId: string;
  role: Role;
  agentAgency: Agency;
  agentBooklets: Booklet[];
  photo?: any;
  technicalreportSurveyorBooklets: Booklet[];
  apeSurveyorBooklets: Booklet[];
  extraSurveyorBooklets: Booklet[];
  secretaryBooklets: Booklet[];
  accountBooklets: Booklet[];
  sellerBooklets: Booklet[];
  accountAgencies: Agency[];
  secretaryAgencies: Agency[];
  agentCode: string;
  iban: string;
  accountNetPrice: number;
  accountantNetPrice: number;
  secretaryNetPrice: number;
  apeNetPrice: number;
  technicalReportNetPrice: number;
  cashPercentage: number;
  hasVat: boolean;
  isCasadocEmployee: boolean;
  userSubRole: EUserSubRole;
  partnerConsent: boolean;
  contracts: StrapiFile[];

  constructor(data: Partial<User> = {}) {
    super({ data });

    if (data.role) {
      this.role = new Role(data.role);
    }

    if (data.agentBooklets) {
      this.agentBooklets = data.agentBooklets.map((booklet) => new Booklet(booklet));
    }
    if (data.technicalreportSurveyorBooklets) {
      this.technicalreportSurveyorBooklets = data.technicalreportSurveyorBooklets.map(
        (booklet) => new Booklet(booklet)
      );
    }
    if (data.apeSurveyorBooklets) {
      this.apeSurveyorBooklets = data.apeSurveyorBooklets.map((booklet) => new Booklet(booklet));
    }
    if (data.extraSurveyorBooklets) {
      this.extraSurveyorBooklets = data.extraSurveyorBooklets.map((booklet) => new Booklet(booklet));
    }
    if (data.secretaryBooklets) {
      this.secretaryBooklets = data.secretaryBooklets.map((booklet) => new Booklet(booklet));
    }
    if (data.accountBooklets) {
      this.accountBooklets = data.accountBooklets.map((booklet) => new Booklet(booklet));
    }
    if (data.sellerBooklets) {
      this.sellerBooklets = data.sellerBooklets.map((booklet) => new Booklet(booklet));
    }
    if (data.accountAgencies) {
      this.accountAgencies = data.accountAgencies.map((agency) => new Agency(agency));
    }
    if (data.secretaryAgencies) {
      this.secretaryAgencies = data.secretaryAgencies.map((agency) => new Agency(agency));
    }

    if (data?.photo) {
      this.photo = new StrapiFile(data.photo);
    }

    if (data?.contracts) {
      this.contracts = data.contracts.map((file) => new StrapiFile(file));
    }
  }

  get isSeller() {
    return this.role?.type === EUsersRole.SELLER;
  }

  get serialised() {
    return {
      id: this.id,
      name: this.name,
      surname: this.surname,
      documentId: this.documentId,
      phoneNumber: this.phoneNumber,
      username: this.username,
      email: this.email,
      provider: this.provider,
      confirmed: this.confirmed,
      blocked: this.blocked,
      publishedAt: this.publishedAt,
      locale: this.locale,
      linkAgentId: this.linkAgentId,
      role: this.role?.serialised,
      agentAgency: this.agentAgency?.serialised,
      agentBooklets: this.agentBooklets?.map((booklet) => booklet.serialised),
      photo: this.photo,
      technicalreportSurveyorBooklets: this.technicalreportSurveyorBooklets?.map(
        (booklet) => booklet.serialised
      ),
      apeSurveyorBooklets: this.apeSurveyorBooklets?.map((booklet) => booklet.serialised),
      extraSurveyorBooklets: this.extraSurveyorBooklets?.map((booklet) => booklet.serialised),
      secretaryBooklets: this.secretaryBooklets?.map((booklet) => booklet.serialised),
      accountBooklets: this.accountBooklets?.map((booklet) => booklet.serialised),
      sellerBooklets: this.sellerBooklets?.map((booklet) => booklet.serialised),
      accountAgencies: this.accountAgencies?.map((agency) => agency.serialised),
      secretaryAgencies: this.secretaryAgencies?.map((agency) => agency.serialised),
      iban: this.iban,
      accountNetPrice: this.accountNetPrice,
      accountantNetPrice: this.accountantNetPrice,
      secretaryNetPrice: this.secretaryNetPrice,
      apeNetPrice: this.apeNetPrice,
      technicalReportNetPrice: this.technicalReportNetPrice,
      cashPercentage: this.cashPercentage,
      hasVat: this.hasVat,
      isCasadocEmployee: this.isCasadocEmployee,
      userSubRole: this.userSubRole,
      partnerConsent: this.partnerConsent,
      contracts: this.contracts?.map((file) => file.serialised),
    };
  }

  get canViewLogs() {
    return true;
  }

  get hasMarcaDaBollo() {
    return !this.hasVat && !this.isSeller;
  }

  get canViewAdmin() {
    return this.checkUserRole([
      EUsersRole.SUPER_ADMIN,
      EUsersRole.SURVEYOR,
      EUsersRole.SECRETARY,
      EUsersRole.ACCOUNT,
      EUsersRole.ACCOUNTANT,
      EUsersRole.PARTNER,
    ]);
  }

  get canViewAdminBooklets() {
    return this.checkUserRole([
      EUsersRole.SUPER_ADMIN,
      EUsersRole.SURVEYOR,
      EUsersRole.SECRETARY,
      EUsersRole.ACCOUNT,
    ]);
  }

  get canViewAdminDashboard() {
    return this.checkUserRole([EUsersRole.SUPER_ADMIN, EUsersRole.ACCOUNTANT]);
  }
  get canViewAdminAccounting() {
    return this.checkUserRole([EUsersRole.SUPER_ADMIN, EUsersRole.ACCOUNTANT]);
  }
  get canViewAdminPartners() {
    return this.checkUserRole([EUsersRole.SUPER_ADMIN, EUsersRole.PARTNER]);
  }

  get canCreateAdminBooklets() {
    return this.checkUserRole([EUsersRole.SUPER_ADMIN, EUsersRole.SECRETARY, EUsersRole.ACCOUNT]);
  }

  get canDeleteAdminBooklets() {
    return this.checkUserRole([EUsersRole.SUPER_ADMIN, EUsersRole.SECRETARY, EUsersRole.ACCOUNT]);
  }

  get canFilterAdminBookletsByGroupAndAgency() {
    return this.checkUserRole([EUsersRole.SUPER_ADMIN]);
  }

  get isSurveyor() {
    return this.role?.type === EUsersRole.SURVEYOR;
  }

  get canViewSurveyorAssignments() {
    return this.checkUserRole([EUsersRole.SURVEYOR]);
  }

  get canViewStrapiLink() {
    return this.checkUserRole([EUsersRole.SUPER_ADMIN]);
  }

  get canViewAdminNewsletter() {
    return this.checkUserRole([EUsersRole.SUPER_ADMIN]);
  }

  get canViewCasadocCalendar() {
    return this.checkUserRole([
      EUsersRole.SUPER_ADMIN,
      EUsersRole.SECRETARY,
      EUsersRole.ACCOUNT,
      EUsersRole.ACCOUNTANT,
    ]);
  }

  get canViewAiApp() {
    return this.checkUserRole([EUsersRole.AGENT, EUsersRole.SUPER_ADMIN]);
  }

  private checkUserRole(roles: EUsersRole[]): boolean {
    return roles.includes(this.role?.type);
  }
}
